import { type HTMLAttributes, type Ref } from "react";

import { cn } from "../../utils";

export type IconSize = "xs" | "sm" | "md" | "lg" | "xl";

export interface IconProps extends HTMLAttributes<HTMLElement> {
  ref?: Ref<HTMLElement>;
  name: KyanIcon;
  size?: IconSize;
  disabled?: boolean;
  hoverEffect?: "circle" | "square";
}

/**
 * Icon component that renders an icon with various sizes and optional click handler.
 *
 * @param name - The name of the icon.
 * @param size - The size of the icon. Can be one of:
 * - "xs": 12px
 * - "sm": 14px
 * - "md": 16px
 * - "lg": 18px
 * - "xl": 20px
 * @param hoverEffect - The hover effect of the icon. Can be one of:
 * - undefined: no hover effect
 * - "circle": circle hover effect
 * - "square": square hover effect
 * @param className - Additional class names to apply to the icon.
 * @param onClick - Optional click handler for the icon.
 */
export const Icon = ({
  name,
  size = "md",
  disabled,
  hoverEffect,
  className,
  onClick,
  ref,
  ...props
}: IconProps) => {
  const hasHoverEffect = hoverEffect !== undefined;

  return (
    <i
      ref={ref}
      {...props}
      aria-hidden="true"
      className={cn(
        `kyan-icon-${name}`,
        {
          "ui:transition-colors ui:hover:bg-buttonHover":
            hasHoverEffect && !disabled,
          "ui:rounded-full": hoverEffect === "circle",
          "ui:rounded-sm": hoverEffect === "square",
        },
        {
          "ui:p-1": hasHoverEffect && size === "xs",
          "ui:p-1.5": hasHoverEffect && size === "sm",
          "ui:p-2": hasHoverEffect && size === "md",
          "ui:p-3": hasHoverEffect && size === "lg",
          "ui:p-3.5": hasHoverEffect && size === "xl",
        },
        {
          "ui:text-xs": size === "xs",
          "ui:text-sm": size === "sm",
          "ui:text-base": size === "md",
          "ui:text-lg": size === "lg",
          "ui:text-xl": size === "xl",
        },
        {
          "ui:cursor-pointer": Boolean(onClick) && !disabled,
        },
        className,
      )}
      onClick={(event) => {
        !disabled && onClick?.(event);
      }}
    />
  );
};

export const kyanIcons = [
  "btc",
  "eth",
  "arb",
  "usdc",
  "caret-up",
  "caret-down",
  "cross",
  "delete",
  "menu",
  "plus",
  "transfer",
  "bell",
  "settings",
  "exit",
  "copy",
  "question",
  "arrow-up",
  "arrow-down",
  "sun",
  "moon",
  "triangle-up",
  "checkmark-circle",
  "delta",
  "gamma",
  "vega",
  "theta",
  "edit",
  "lock",
  "attention",
  "key",
  "one-click",
  "error-circle",
  "trophy",
  "user",
  "notice",
  "lock-2",
  "navigation",
  "package",
  "clock",
  "refresh",
  "construction",
  "pointer-arrow",
  "download",
  "x",
  "link",
  "share",
  "help",
  "collapse",
  "expand",
  "reload",
  "kyan",
  "archetype",
  "premia",
  "options",
  "portfolio",
  "candles",
  "bridge",
  "surprise",
  "filter",
  "checkmark",
  "eye",
  "navigation-2",
] as const;
export type KyanIcon = (typeof kyanIcons)[number];
